import {
    ButtonGradientPrimary,
    ButtonSecondary,
    Card,
    Colors,
    Heading,
    Icon,
    RichText,
    SelectText,
    Spinner,
    TextInput,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@amelio/ui-library';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';

import type { TranslationKey } from '@/i18n-key';
import type { ColorType, IconProps, RichTextSurface } from '@amelio/ui-library';
import type { CSSProperties } from 'react';
import type { Descendant } from 'slate';

import { companyAtom } from '@/atoms/company';
import { userInstanceAtom } from '@/atoms/userDetails';
import { FormField } from '@/components/FormField/FormField';
import { EDITOR_TOOLBAR_DENSITY } from '@/constants/RichTextEditor';
import { useAmelioTranslations } from '@/hooks';
import i18n from '@/plugins/i18n';
import {
    localizeMergeTagsForUi,
    localizeMergeTagsForUiSlate,
    normalizeMergeTagsForStorage,
    normalizeMergeTagsForStorageSlate,
} from '@/utils/emailMergeTags';
import { getLanguageDisplayName } from '@/utils/localization';

const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

interface TextNode {
    text: string;
}

interface ElementNode {
    children: (TextNode | ElementNode)[];
}

const getCharacterCount = (value: Descendant[]): number => {
    return value
        .map((node) => {
            const element = node as ElementNode;
            return element.children
                .map((child) => {
                    if ('text' in child) {
                        return (child as TextNode).text;
                    }
                    return '';
                })
                .join('');
        })
        .join('').length;
};

type CustomizeSurveyEmailSkin = 'default' | 'cycleCommunications';

interface EmailSkinStyles {
    cardVariant: 'default' | 'flat';
    cardPadding: 'none' | 'xl';
    cardClassName: string;
    hasModuleHeader: boolean;
    fieldsClassName: string;
    subjectRowClassName: string;
    subjectFieldClassName: string;
    subjectFieldChrome: string;
    languageFieldClassName: string;
    languageSelectTriggerStyle?: CSSProperties;
    languageSelectSize: 'sm' | 'md' | 'lg';
    /** The size of the text in the closed selector, when the field's own size would give another. */
    languageSelectTextSize?: 'sm' | 'md' | 'lg';
    languageSelectTriggerClassName?: string;
    languageSelectHideSelected: boolean;
    languageSelectShowIndicator: boolean;
    languageSelectOptionTextSize: 'default' | 'lg';
    messageFieldClassName: string;
    labelSize: 'small' | 'default' | 'large';
    labelTransform: 'none' | 'uppercase';
    labelWeight: 'medium' | 'bold';
    languageLabelKey: TranslationKey;
    subjectLabelKey: TranslationKey;
    richTextSurface: RichTextSurface;
    footerClassName: string;
    subjectPlaceholderKey: TranslationKey;
    testEmailFieldVariant: 'default' | 'white';
    testEmailFieldChrome: string;
    testButtonLabelClassName: string;
    testButtonIconName: IconProps['name'];
    testButtonIconColor: ColorType;
    testButtonIconStrokeWidth?: number;
}

const EMAIL_SKIN_STYLES: Record<CustomizeSurveyEmailSkin, EmailSkinStyles> = {
    default: {
        cardVariant: 'default',
        cardPadding: 'xl',
        cardClassName: 'mt-12',
        hasModuleHeader: true,
        fieldsClassName: 'mt-4 flex flex-col gap-2 md:mt-10',
        subjectRowClassName: 'contents',
        subjectFieldClassName: '',
        subjectFieldChrome: '',
        languageFieldClassName: '',
        languageSelectTriggerStyle: undefined,
        languageSelectSize: 'md',
        languageSelectTextSize: undefined,
        languageSelectTriggerClassName: undefined,
        languageSelectHideSelected: true,
        languageSelectShowIndicator: false,
        languageSelectOptionTextSize: 'default',
        messageFieldClassName: 'mt-6',
        labelSize: 'large',
        labelTransform: 'none',
        labelWeight: 'medium',
        languageLabelKey: 'evaluation360ScheduleCreation.customizeSurveyEmail.selectLanguage',
        subjectLabelKey: 'evaluation360ScheduleCreation.customizeSurveyEmail.titleLabel',
        richTextSurface: 'default',
        footerClassName:
            'mt-8 flex flex-col items-start justify-end gap-5 sm:mt-5 md:flex-row md:items-center md:gap-8',
        subjectPlaceholderKey: 'evaluation360ScheduleCreation.customizeSurveyEmail.enter',
        testEmailFieldVariant: 'default',
        testEmailFieldChrome: '',
        testButtonLabelClassName: '',
        testButtonIconName: 'messages-sms-tracking',
        testButtonIconColor: Colors.BASE,
    },
    cycleCommunications: {
        cardVariant: 'flat',
        cardPadding: 'none',
        cardClassName: 'px-7 pt-6 pb-6',
        hasModuleHeader: false,
        fieldsClassName: 'flex flex-col gap-6',
        subjectRowClassName: 'flex flex-col gap-4 md:flex-row md:items-start',
        subjectFieldClassName: 'min-w-0 flex-1',
        subjectFieldChrome: 'border-step-rail-track',
        languageFieldClassName: 'shrink-0',
        languageSelectTriggerStyle: {
            backgroundColor: 'var(--color-base-25)',
            borderStyle: 'solid',
            borderColor: 'var(--color-step-rail-track)',
        },
        languageSelectSize: 'lg',
        languageSelectTextSize: 'sm',
        languageSelectTriggerClassName: 'whitespace-nowrap',
        languageSelectHideSelected: false,
        languageSelectShowIndicator: true,
        languageSelectOptionTextSize: 'lg',
        messageFieldClassName: '',
        labelSize: 'small',
        labelTransform: 'uppercase',
        labelWeight: 'bold',
        languageLabelKey: 'cycleEvaluationCommunications.email.languageLabel',
        subjectLabelKey: 'cycleEvaluationCommunications.email.subjectLabel',
        richTextSurface: 'framed',
        footerClassName:
            'border-base-50 -mx-7 -mb-6 mt-2 flex flex-col items-start justify-end gap-3 border-t bg-base-25 px-7 py-4 md:flex-row md:items-center',
        subjectPlaceholderKey: 'cycleEvaluationCommunications.email.subjectPlaceholder',
        testEmailFieldVariant: 'white',
        testEmailFieldChrome: 'border-step-rail-track',
        testButtonLabelClassName: 'font-semibold',
        testButtonIconName: 'essential-send-plane',
        testButtonIconColor: Colors.WHITE,
        testButtonIconStrokeWidth: 2,
    },
};

interface CustomizeSurveyEmailProps {
    emailLanguage: string;
    emailTitle: string;
    emailMessage: Descendant[];
    editorKey?: string;
    testEmailAddress: string;
    onEmailLanguageChange: (value: string) => void;
    onEmailTitleChange: (value: string) => void;
    onEmailMessageChange: (value: Descendant[]) => void;
    onTestEmailAddressChange: (value: string) => void;
    onTestEmail: () => void;
    isSendingTestEmail: boolean;
    customizeSurveyEmailHeader?: string;
    testLaunchEmailLabel?: string;
    isReadOnly?: boolean;
    skin?: CustomizeSurveyEmailSkin;
    unavailableReason?: string;
}

const CustomizeSurveyEmail = ({
    emailLanguage,
    emailTitle,
    emailMessage,
    editorKey,
    testEmailAddress,
    onEmailLanguageChange,
    onEmailTitleChange,
    onEmailMessageChange,
    onTestEmailAddressChange,
    onTestEmail,
    isSendingTestEmail,
    customizeSurveyEmailHeader,
    testLaunchEmailLabel,
    isReadOnly = false,
    skin = 'default',
    unavailableReason,
}: CustomizeSurveyEmailProps) => {
    const { t } = useAmelioTranslations();
    const userDetail = useAtomValue(userInstanceAtom);
    const companyDetail = useAtomValue(companyAtom);

    const displayEmailTitle = useMemo(
        () => localizeMergeTagsForUi(emailTitle, emailLanguage),
        [emailTitle, emailLanguage],
    );

    const displayEmailMessage = useMemo(
        () => localizeMergeTagsForUiSlate(emailMessage, emailLanguage),
        [emailMessage, emailLanguage],
    );

    const languageOptions = useMemo(() => {
        const languageArray =
            (userDetail?.surveyLanguages?.length ?? 0) > 0
                ? userDetail?.surveyLanguages || []
                : companyDetail?.company?.surveyLanguages || [];

        return languageArray.map((lang) => ({
            label: getLanguageDisplayName(lang),
            value: lang,
        }));
    }, [userDetail, companyDetail]);

    const handleEmailTitleChange = useCallback(
        (value: string) => {
            onEmailTitleChange(normalizeMergeTagsForStorage(value));
        },
        [onEmailTitleChange],
    );

    const handleEmailMessageChange = useCallback(
        (value: Descendant[]) => {
            onEmailMessageChange(normalizeMergeTagsForStorageSlate(value));
        },
        [onEmailMessageChange],
    );

    const messageCharacterCount = getCharacterCount(displayEmailMessage);

    const isTestEmailBlockedByInput =
        isSendingTestEmail ||
        !!unavailableReason ||
        !testEmailAddress ||
        !isValidEmail(testEmailAddress) ||
        emailTitle.trim().length < 5 ||
        messageCharacterCount === 0;

    const isTestEmailDisabled = isReadOnly || isTestEmailBlockedByInput;

    const resolvedTestLaunchEmailLabel =
        testLaunchEmailLabel ??
        t('evaluation360ScheduleCreation.customizeSurveyEmail.testLaunchEmail');

    const getTooltipMessage = () => {
        if (unavailableReason) {
            return unavailableReason;
        }
        if (isSendingTestEmail) {
            return t('evaluation360ScheduleCreation.customizeSurveyEmail.testEmailSending');
        }
        if (!testEmailAddress) {
            return t('evaluation360ScheduleCreation.customizeSurveyEmail.testEmailRequired');
        }
        if (!isValidEmail(testEmailAddress)) {
            return t('evaluation360ScheduleCreation.customizeSurveyEmail.testEmailInvalid');
        }
        if (emailTitle.trim().length < 5) {
            return t('evaluation360ScheduleCreation.customizeSurveyEmail.testEmailTitleMin');
        }
        if (messageCharacterCount === 0) {
            return t('evaluation360ScheduleCreation.customizeSurveyEmail.testEmailMessageRequired');
        }
        return '';
    };

    const skinStyles = EMAIL_SKIN_STYLES[skin];
    const TestEmailButton = skin === 'default' ? ButtonSecondary : ButtonGradientPrimary;

    const languageSelect = (
        <SelectText
            placeholder={t('evaluation360ScheduleCreation.customizeSurveyEmail.selectLanguage')}
            value={emailLanguage}
            hideSelectedOption={skinStyles.languageSelectHideSelected}
            readOnly={isReadOnly}
            onChange={(value) => onEmailLanguageChange(value)}
            options={languageOptions}
            sort={'asc'}
            size={skinStyles.languageSelectSize}
            textSize={skinStyles.languageSelectTextSize}
            triggerStyle={skinStyles.languageSelectTriggerStyle}
            triggerClassName={skinStyles.languageSelectTriggerClassName}
            showSelectedIndicator={skinStyles.languageSelectShowIndicator}
            optionTextSize={skinStyles.languageSelectOptionTextSize}
        />
    );

    const testEmailButton = (
        <TestEmailButton
            size={'md'}
            onClick={onTestEmail}
            disabled={isTestEmailDisabled}
        >
            {isSendingTestEmail ? (
                <Spinner size={'xs'} />
            ) : (
                <>
                    <Icon
                        name={skinStyles.testButtonIconName}
                        size={'md'}
                        className={'mr-2'}
                        color={skinStyles.testButtonIconColor}
                        strokeWidth={skinStyles.testButtonIconStrokeWidth}
                    />

                    <span className={skinStyles.testButtonLabelClassName}>
                        {resolvedTestLaunchEmailLabel}
                    </span>
                </>
            )}
        </TestEmailButton>
    );

    return (
        <Card
            className={skinStyles.cardClassName}
            bgColor={'bg-white'}
            borderRadius={'2xl'}
            padding={skinStyles.cardPadding}
            variant={skinStyles.cardVariant}
        >
            {skinStyles.hasModuleHeader && (
                <div
                    className={
                        'flex flex-col items-start md:flex-row md:items-center md:justify-between'
                    }
                >
                    <div className={'mb-4 flex items-start md:mb-0 md:items-center'}>
                        <Icon
                            name={'messages-sms'}
                            size={'xl'}
                            className={'mr-3'}
                        />

                        <Heading level={4}>
                            {customizeSurveyEmailHeader ||
                                t('evaluation360ScheduleCreation.customizeSurveyEmail.title')}
                        </Heading>
                    </div>

                    {languageSelect}
                </div>
            )}

            <div className={skinStyles.fieldsClassName}>
                <div className={skinStyles.subjectRowClassName}>
                    <FormField
                        label={t(skinStyles.subjectLabelKey)}
                        labelSize={skinStyles.labelSize}
                        labelTransform={skinStyles.labelTransform}
                        labelWeight={skinStyles.labelWeight}
                        className={skinStyles.subjectFieldClassName}
                    >
                        <TextInput
                            placeholder={t(skinStyles.subjectPlaceholderKey)}
                            value={displayEmailTitle}
                            onChange={handleEmailTitleChange}
                            readOnly={isReadOnly}
                            size={'lg'}
                            className={skinStyles.subjectFieldChrome}
                        />
                    </FormField>

                    {!skinStyles.hasModuleHeader && (
                        <FormField
                            label={t(skinStyles.languageLabelKey)}
                            labelSize={skinStyles.labelSize}
                            labelTransform={skinStyles.labelTransform}
                            labelWeight={skinStyles.labelWeight}
                            className={skinStyles.languageFieldClassName}
                        >
                            {languageSelect}
                        </FormField>
                    )}
                </div>

                <FormField
                    label={t('evaluation360ScheduleCreation.customizeSurveyEmail.message')}
                    labelSize={skinStyles.labelSize}
                    labelTransform={skinStyles.labelTransform}
                    labelWeight={skinStyles.labelWeight}
                    className={skinStyles.messageFieldClassName}
                >
                    <RichText
                        toolbarDensity={EDITOR_TOOLBAR_DENSITY}
                        key={editorKey}
                        locale={i18n.language}
                        placeholder={t(
                            'evaluation360ScheduleCreation.customizeSurveyEmail.typeHere',
                        )}
                        value={displayEmailMessage}
                        onChange={handleEmailMessageChange}
                        readOnly={isReadOnly}
                        maxLength={3000}
                        showCharacterCount={true}
                        surface={skinStyles.richTextSurface}
                        contentTextSize={'lg'}
                    />
                </FormField>

                <div className={skinStyles.footerClassName}>
                    <TextInput
                        className={`md:w-64 ${skinStyles.testEmailFieldChrome}`}
                        placeholder={t(
                            'evaluation360ScheduleCreation.customizeSurveyEmail.testEmailAddressPlaceholder',
                        )}
                        value={testEmailAddress}
                        onChange={onTestEmailAddressChange}
                        readOnly={isReadOnly}
                        type={'email'}
                        variant={skinStyles.testEmailFieldVariant}
                        required
                    />

                    {isTestEmailBlockedByInput ? (
                        <TooltipProvider>
                            <Tooltip delayDuration={200}>
                                <TooltipTrigger asChild={true}>
                                    <div className={'inline-flex'}>{testEmailButton}</div>
                                </TooltipTrigger>

                                <TooltipContent side={'bottom'}>
                                    {getTooltipMessage()}
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    ) : (
                        testEmailButton
                    )}
                </div>
            </div>
        </Card>
    );
};

export { CustomizeSurveyEmail };
export type { CustomizeSurveyEmailProps };
